/**
 * Utility functions for tenant/subdomain detection and resolution
 */

/**
 * Extract subdomain from hostname
 * Examples:
 * - acme.crudexpert.com → "acme"
 * - acme.localhost:3000 → "acme"
 * - localhost:3000 → null
 * - crudexpert.com → null
 */
const isReservedSubdomain = (subdomain: string | null | undefined): boolean => {
  if (!subdomain) return false;
  const lower = subdomain.toLowerCase();
  return lower === 'www' || lower === 'api' || lower === 'app';
};

export const extractSubdomain = (hostname: string): string | null => {
  if (!hostname) return null;

  // Remove port if present
  const hostWithoutPort = hostname.split(':')[0];

  // Handle localhost subdomains (e.g., acme.localhost)
  if (hostWithoutPort.includes('.localhost')) {
    const parts = hostWithoutPort.split('.');
    if (parts.length > 1 && parts[0] !== 'localhost' && !isReservedSubdomain(parts[0])) {
      return parts[0];
    }
    return null;
  }

  // Handle production domains (e.g., acme.crudexpert.com)
  const parts = hostWithoutPort.split('.');
  
  // If we have at least 3 parts (subdomain.domain.tld) or 2 parts for localhost
  if (parts.length >= 3) {
    const subdomain = parts[0];
    // Skip reserved subdomains as they are not tenant subdomains
    if (!isReservedSubdomain(subdomain)) {
      return subdomain;
    }
  }

  return null;
};

/**
 * Check if current domain is main domain (not a subdomain)
 */
export const isMainDomain = (): boolean => {
  const subdomain = extractSubdomain(window.location.hostname);
  return subdomain === null;
};

/**
 * Get current tenant slug from hostname
 */
export const getCurrentTenantSlug = (): string | null => {
  return extractSubdomain(window.location.hostname);
};

/**
 * Get tenant slug from query parameter (for development)
 */
export const getTenantFromQuery = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  return params.get('tenant');
};

/**
 * Tenant slug to send on /auth/* requests so the backend binds login to the workspace
 * shown in the browser (not the user's primary org on the marketing domain).
 * On tenant subdomains (e.g. gmail.localhost), the URL host always wins over dev_tenant override.
 */
export const getTenantHintForApiRequest = (): string | null => {
  if (typeof window === 'undefined') return null;
  const hostSlug = extractSubdomain(window.location.hostname);
  if (hostSlug) {
    return hostSlug;
  }
  try {
    const q = getTenantFromQuery();
    if (q && q.trim()) return q.trim();
  } catch {
    /* ignore */
  }
  if (process.env.NODE_ENV === 'development') {
    const st = getTenantFromStorage();
    if (st && st.trim()) return st.trim();
  }
  return null;
};

/** Subdomain from the current browser host (ignores dev_tenant localStorage override). */
export const getHostTenantSlug = (): string | null => {
  if (typeof window === 'undefined') return null;
  const slug = extractSubdomain(window.location.hostname);
  return slug ? slug.toLowerCase() : null;
};

/**
 * Get tenant slug from localStorage (for development override)
 */
export const getTenantFromStorage = (): string | null => {
  return localStorage.getItem('dev_tenant');
};

/**
 * Get tenant slug with fallback priority:
 * 1. Subdomain from hostname (tenant URL always wins over dev overrides)
 * 2. Query parameter (development, main domain only)
 * 3. localStorage dev_tenant (development, main domain only)
 */
export const getTenantSlug = (): string | null => {
  const hostSlug = extractSubdomain(window.location.hostname);
  if (hostSlug) {
    return hostSlug;
  }
  if (process.env.NODE_ENV === 'development') {
    const queryTenant = getTenantFromQuery();
    if (queryTenant) return queryTenant;

    const storageTenant = getTenantFromStorage();
    if (storageTenant) return storageTenant;
  }

  return null;
};

/**
 * Build tenant subdomain URL
 */
export const buildTenantUrl = (tenantSlug: string, path: string = ''): string => {
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port ? `:${window.location.port}` : '';

  // For localhost, use *.localhost pattern
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return `${protocol}//${tenantSlug}.localhost${port}${path}`;
  }

  // For production, replace subdomain
  const domainParts = hostname.split('.');
  if (domainParts.length >= 2) {
    const domain = domainParts.slice(-2).join('.'); // Get domain.tld
    return `${protocol}//${tenantSlug}.${domain}${port}${path}`;
  }

  return `${protocol}//${tenantSlug}.${hostname}${port}${path}`;
};

/**
 * Build URL for the main app subdomain.
 * - Localhost: app.localhost
 * - Production: app.crudexpert.com (or app.{domain})
 */
export const buildAppUrl = (path: string = '/'): string => {
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port ? `:${window.location.port}` : '';

  // For localhost, always use app.localhost
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return `${protocol}//app.localhost${port}${path}`;
  }

  const parts = hostname.split('.');
  if (parts.length >= 2) {
    const domain = parts.slice(-2).join('.'); // domain.tld
    return `${protocol}//app.${domain}${port}${path}`;
  }

  // Fallback: just prefix current host with app.
  return `${protocol}//app.${hostname}${port}${path}`;
};

export const redirectToApp = (path: string = '/'): void => {
  window.location.href = buildAppUrl(path);
};

/**
 * Local dev hosts (plain localhost, tenant.localhost, loopback). Session-replace redirects are often
 * benign here (same machine, hot reload) — skip alarming "another device" messaging.
 */
export const isLocalDevHostname = (hostname: string): boolean => {
  if (!hostname) return false;
  const h = hostname.split(':')[0].toLowerCase();
  return h === 'localhost' || h === '127.0.0.1' || h.endsWith('.localhost');
};

/**
 * Redirect to tenant subdomain with optional exchange token
 */
export const redirectToTenant = (tenantSlug: string, path: string = '/', exchangeToken?: string): void => {
  let url = buildTenantUrl(tenantSlug, path);
  
  // If exchange token is provided, add it to the URL
  if (exchangeToken) {
    const params = new URLSearchParams();
    params.set('exchangeToken', exchangeToken);
    url += `${url.includes('?') ? '&' : '?'}${params.toString()}`;
  }
  
  window.location.href = url;
};

